import { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Upload,
  X,
  Loader2,
  CheckCircle,
  Lock,
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  Home,
  Building2,
  LandPlot,
  Hammer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ContractWiserLogo } from "@/components/ContractWiserLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const propertyTypes = [
  { value: "house", label: "House", icon: Home },
  { value: "unit", label: "Unit / Apartment", icon: Building2 },
  { value: "land", label: "Vacant Land", icon: LandPlot },
  { value: "off-the-plan", label: "Off-the-Plan", icon: Hammer }
];

const states = [
  { value: "NSW", label: "New South Wales (NSW)" },
  { value: "VIC", label: "Victoria (VIC)" },
  { value: "QLD", label: "Queensland (QLD)" },
  { value: "SA", label: "South Australia (SA)" },
  { value: "WA", label: "Western Australia (WA)" },
  { value: "TAS", label: "Tasmania (TAS)" },
  { value: "ACT", label: "Australian Capital Territory (ACT)" },
  { value: "NT", label: "Northern Territory (NT)" }
];

export default function UploadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [file, setFile] = useState(null);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [concern, setConcern] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Check if redirected from cancelled payment
  useEffect(() => {
    document.title = "Upload Contract | ContractWiser";
    const cancelled = searchParams.get('cancelled');
    const submissionId = searchParams.get('submission_id');
    if (cancelled === 'true') {
      toast.error("Payment was cancelled. You can try again when you're ready.");
    }
  }, [searchParams]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      toast.error("Please upload a PDF file only.");
      return;
    }
    if (acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      if (uploadedFile.size > 20 * 1024 * 1024) {
        toast.error("File size must be less than 20MB.");
        return;
      }
      setFile(uploadedFile);
      toast.success("File selected — fill in the details below and click Continue to Payment.");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1
  });

  const removeFile = () => {
    setFile(null);
  };

  const validateForm = () => {
    if (!file) {
      toast.error("Please upload your contract PDF.");
      return false;
    }
    if (!email || !email.includes('@')) {
      toast.error("Please enter a valid email address.");
      return false;
    }
    if (!state) {
      toast.error("Please select your property state.");
      return false;
    }
    if (!propertyType) {
      toast.error("Please select your property type.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('email', email);
      formData.append('state', state);
      formData.append('property_type', propertyType);
      if (concern) {
        formData.append('concern', concern);
      }

      // Upload the contract
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      const submissionId = uploadResponse.data.submission_id;
      toast.success("Contract uploaded! Analyzing your contract...");

      setIsUploading(false);
      setIsProcessing(true);

      // Redirect to report page (analysis starts automatically, payment unlocks full report)
      navigate(`/report?submission_id=${submissionId}&email=${encodeURIComponent(email)}`);

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || "Failed to upload contract. Please try again.";
      
      // Check if it's a scanned PDF error - show a more helpful message
      if (errorMessage.includes('scanned image') || errorMessage.includes('no extractable text')) {
        toast.error("Scanned PDF Detected", {
          description: "Your PDF appears to be a scanned image. Please upload a text-based PDF or run OCR using Adobe Acrobat first.",
          duration: 10000,
        });
      } else {
        toast.error(errorMessage);
      }
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="hover:opacity-80 transition-opacity"
          >
            <ContractWiserLogo size="default" />
          </button>
          <Button 
            data-testid="back-to-home-btn"
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-eucalypt-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-eucalypt-800 mb-3">
                Upload Your Contract
              </h1>
              <p className="text-muted-foreground text-lg">
                Upload your property contract and we'll analyze it for you.
              </p>
            </div>

            {/* Upload Form Card */}
            <Card className="shadow-float border-0 overflow-hidden">
              <CardHeader className="bg-eucalypt-800 text-white px-8 py-6">
                <CardTitle className="font-heading flex items-center gap-3">
                  <Upload className="h-5 w-5" />
                  Contract Upload Form
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8 space-y-8">
                {/* File Upload */}
                <div>
                  <Label className="text-eucalypt-800 font-semibold mb-3 block">
                    Contract PDF *
                  </Label>
                  {!file ? (
                    <div
                      {...getRootProps()}
                      data-testid="file-dropzone"
                      className={`border-2 border-dashed p-10 text-center cursor-pointer transition-all duration-200 ${
                        isDragActive 
                          ? 'border-terracotta-500 bg-terracotta-50' 
                          : 'border-sandstone-400 hover:border-eucalypt-500 hover:bg-sandstone-50'
                      }`}
                    >
                      <input {...getInputProps()} data-testid="file-input" />
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-sandstone-200 flex items-center justify-center">
                          <Upload className="h-8 w-8 text-eucalypt-600" />
                        </div>
                        <div>
                          <p className="text-eucalypt-700 font-semibold mb-1">
                            {isDragActive ? "Drop your contract here" : "Drag & drop your contract PDF"}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            or click to browse (max 20MB)
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="border border-eucalypt-200 bg-eucalypt-50 p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-eucalypt-200 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-eucalypt-700" />
                        </div>
                        <div>
                          <p className="font-semibold text-eucalypt-800 truncate max-w-xs">
                            {file.name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {file.size < 1024 * 1024 
                              ? `${(file.size / 1024).toFixed(1)} KB` 
                              : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                          </p>
                        </div>
                      </div>
                      <button
                        data-testid="remove-file-btn"
                        onClick={removeFile}
                        className="p-2 hover:bg-eucalypt-200 rounded transition-colors"
                      >
                        <X className="h-5 w-5 text-eucalypt-600" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-eucalypt-800 font-semibold mb-3 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border-sandstone-300 focus:border-eucalypt-500 focus:ring-eucalypt-500"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    We'll send a copy of your report to this email.
                  </p>
                </div>

                {/* State */}
                <div>
                  <Label className="text-eucalypt-800 font-semibold mb-3 block">
                    Property State *
                  </Label>
                  <Select value={state} onValueChange={setState}>
                    <SelectTrigger 
                      data-testid="state-select"
                      className="border-sandstone-300 focus:border-eucalypt-500 focus:ring-eucalypt-500"
                    >
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((s) => (
                        <SelectItem key={s.value} value={s.value} data-testid={`state-${s.value}`}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Property Type */}
                <div>
                  <Label className="text-eucalypt-800 font-semibold mb-3 block">
                    Property Type *
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {propertyTypes.map((type) => (
                      <button
                        key={type.value}
                        data-testid={`property-type-${type.value}`}
                        onClick={() => setPropertyType(type.value)}
                        className={`p-4 border-2 text-left transition-all duration-200 flex items-center gap-3 ${
                          propertyType === type.value
                            ? 'border-eucalypt-600 bg-eucalypt-50'
                            : 'border-sandstone-300 hover:border-eucalypt-400'
                        }`}
                      >
                        <type.icon className={`h-5 w-5 ${
                          propertyType === type.value ? 'text-eucalypt-700' : 'text-muted-foreground'
                        }`} />
                        <span className={propertyType === type.value ? 'text-eucalypt-800 font-semibold' : 'text-muted-foreground'}>
                          {type.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Concerns (Optional) */}
                <div>
                  <Label htmlFor="concern" className="text-eucalypt-800 font-semibold mb-3 block">
                    What are you most concerned about? (Optional)
                  </Label>
                  <Textarea
                    id="concern"
                    data-testid="concern-textarea"
                    placeholder="e.g., The settlement timeline seems short, or I'm not sure about the special conditions..."
                    value={concern}
                    onChange={(e) => setConcern(e.target.value)}
                    className="border-sandstone-300 focus:border-eucalypt-500 focus:ring-eucalypt-500 min-h-[100px]"
                  />
                </div>

                {/* Privacy Notice */}
                <div className="bg-sandstone-100 p-4 flex items-start gap-3">
                  <Lock className="h-5 w-5 text-eucalypt-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Your uploaded document is used only to generate your report and is 
                    automatically deleted after processing. We do not store, share, or 
                    retain your contract files.
                  </p>
                </div>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Uploading...</span>
                      <span className="text-eucalypt-700 font-semibold">{uploadProgress}%</span>
                    </div>
                    <div className="h-2 bg-sandstone-200 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-eucalypt-600"
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  data-testid="submit-upload-btn"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={isUploading || isProcessing}
                  className="w-full bg-terracotta-600 hover:bg-terracotta-700 text-white text-lg py-6"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : isProcessing ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Upload & Analyze
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </>
                  )}
                </Button>

                {/* Price Info */}
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-eucalypt-600" />
                  <span>Free preview — unlock full report for $49.99 AUD</span>
                </div>
              </CardContent>
            </Card>

            {/* Supported States Notice */}
            <div className="mt-8 text-center">
              <p className="text-muted-foreground text-sm">
                Currently supporting all Australian states and territories.
              </p>
              <p className="text-muted-foreground text-sm mt-1">
                Other countries coming soon. <button onClick={() => navigate('/contact')} className="text-eucalypt-700 hover:underline">Contact us</button> for manual processing.
              </p>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
